import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { SERVICES } from 'utils/service-entitlements-config';

// components
import AppErrorBoundary from 'app/error-boundary';
import Layout from 'components/Layout/Layout';
import BrandLoader from 'components/Layout/BrandLoader';
import { PageLoader } from 'components/Spin';

// route guards
import AuthRoute from './AuthRoute';
import UnAuthRoute from './UnAuthRoute';
import PrivateRoute from './PrivateRoute';

// Lazy-loaded pages
const Dashboard = lazy(() => import('pages/dashboard'));
const PasswordScan = lazy(() => import('pages/password-scan'));
const ThinEnrollment = lazy(() => import('pages/enrollment/thin-enrollment'));
const FiveStepEnrollment = lazy(
  () => import('pages/enrollment/five-step-enrollment'),
);
const FlexEnrollment = lazy(() => import('pages/enrollment/flex-enrollment'));
const LightEnrollment = lazy(() => import('pages/enrollment/light-enrollment'));
const CreatingDashboard = lazy(
  () => import('pages/enrollment/CreatingDashboard/v1'),
);
const SignIn = lazy(() => import('pages/signin'));
const ForgotPassword = lazy(() => import('pages/forgot-password'));
const CheckYourEmail = lazy(() => import('pages/check-your-email'));
const VerifyYourIdentity = lazy(() => import('pages/verify-your-identity'));
const CreateNewPassword = lazy(() => import('pages/create-new-password'));
const ResetPassword = lazy(() => import('pages/reset-password'));
const Alerts = lazy(() => import('pages/alerts'));
const EnrollmentFAQ = lazy(() => import('pages/enrollment-faq'));
const DarkWebWatchlist = lazy(() => import('pages/dark-web-watchlist'));
const DataBrokerOptOut = lazy(() => import('pages/data-broker-opt-out'));
const VpnInTouch = lazy(() => import('pages/vpn-in-touch'));
const PasswordDefense = lazy(() => import('pages/password-defense'));
const ThankYouDownload = lazy(() => import('pages/thank-you-download'));
const Passwords = lazy(() => import('pages/vault/passwords'));
const EmailVerification = lazy(() => import('pages/email-verification'));
const Support = lazy(() => import('pages/support'));
const DigitalVault = lazy(() => import('pages/vault/digital-vault'));
const DigitalVaultLayout = lazy(
  () => import('pages/vault/digital-vault/layout'),
);
const DocumentDetails = lazy(
  () => import('pages/vault/digital-vault/partials/document-details'),
);
const MyAccount = lazy(() => import('pages/myaccount'));
const MyPlan = lazy(() => import('pages/myplan'));
const FAQS = lazy(() => import('pages/faqs'));
const AccountDetails = lazy(() => import('pages/account-details'));
const AccountDetailsView = lazy(
  () => import('pages/vault/passwords/partials/accountDetailsView'),
);
const NotificationSettings = lazy(() => import('pages/notification-settings'));
const TopicFAQ = lazy(() => import('pages/faqs/partials/topic-faq'));
const VaultDetails = lazy(
  () => import('pages/myaccount/partials/vault-details'),
);
const VerifyToken = lazy(() => import('pages/verify-token'));
const TermsOfService = lazy(() => import('pages/terms-of-service'));
const PrivacyPolicy = lazy(() => import('pages/privacy-policy'));
const CompanyCode = lazy(() => import('pages/enrollment/CompanyCode'));
const DownloadPasswordDefense = lazy(
  () => import('pages/password-defense/partials/download-password-defense'),
);
const Insurance = lazy(() => import('pages/insurance'));
const MedicalDataMonitoring = lazy(
  () => import('pages/medical-data-monitoring'),
);
const SecureDocumentUpload = lazy(() => import('pages/secure-document-upload'));
const Security = lazy(() => import('pages/security'));
const HomeTitleMonitoring = lazy(() => import('pages/home-title-monitoring'));
const HtmonRegisteredPropertyDetails = lazy(
  () => import('pages/home-title-monitoring/registered-property-details'),
);
const CreditScore = lazy(() => import('pages/credit-score'));
const CreditScoreTracker = lazy(() => import('pages/credit-score-tracker'));
const CreditLock = lazy(() => import('pages/credit-lock'));

/* Error pages */
const ErrorPageLayout = lazy(() => import('pages/error-page'));
const SomethingWentWrong = lazy(
  () => import('pages/error-page/something-went-wrong'),
);
const OfferNotFound = lazy(() => import('pages/error-page/offer-not-found'));
const BrandNotMatched = lazy(
  () => import('pages/error-page/brand-not-matched'),
);

const routes = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <AppErrorBoundary />,
    children: [
      {
        element: <AuthRoute />,
        children: [
          {
            index: true,
            element: <Navigate to='dashboard' replace />,
          },
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: 'alerts',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.alerts}>
                  <Alerts />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'vpn-in-touch',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.vpnInTouch}>
                  <VpnInTouch />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'passwords',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.passwords}>
                      <Passwords />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.passwords}>
                      <AccountDetailsView />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'digital-vault',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.digitalVault}>
                  <DigitalVaultLayout />
                </PrivateRoute>
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DigitalVault />
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DocumentDetails />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'my-account',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MyAccount />
                  </Suspense>
                ),
              },
              {
                path: 'plan-details',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MyPlan />
                  </Suspense>
                ),
              },
              {
                path: 'account-details',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountDetails />
                  </Suspense>
                ),
              },
              {
                path: 'notification-settings',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <NotificationSettings />
                  </Suspense>
                ),
              },
              {
                path: 'vault-details',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.digitalVault}>
                      <VaultDetails />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: 'secure-document-upload',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SecureDocumentUpload />
                  </Suspense>
                ),
              },
              {
                path: 'security',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Security />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'support',
            element: (
              <Suspense fallback={<PageLoader />}>
                <Support />
              </Suspense>
            ),
          },
          {
            path: 'password-scan',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.passwordScan}>
                  <PasswordScan />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'medical-data-monitoring',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.medicalDataMonitoring}>
                  <MedicalDataMonitoring />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'dark-web',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.darkweb}>
                  <DarkWebWatchlist />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'data-broker-opt-out',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.dboo}>
                  <DataBrokerOptOut />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'password-defense',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute
                  service={SERVICES.passwords}
                  agentCheckRequired={false}
                >
                  <PasswordDefense />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'insurance',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.insurance}>
                  <Insurance />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'home-title-monitoring',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.htmon}>
                      <HomeTitleMonitoring />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: ':addressId',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.htmon}>
                      <HtmonRegisteredPropertyDetails />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'credit-score',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.creditScore}>
                  <CreditScore />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'credit-tracker',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.creditScore}>
                  <CreditScoreTracker />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'credit-lock',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.creditLock}>
                  <CreditLock />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'faqs',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FAQS />
                  </Suspense>
                ),
              },
              {
                path: ':topic',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TopicFAQ />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to='dashboard' replace />,
          },
        ],
      },
      {
        element: <UnAuthRoute />,
        children: [
          {
            path: 'enrollment',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ThinEnrollment />
                  </Suspense>
                ),
              },
              {
                path: ':companyCode',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <CompanyCode />
                  </Suspense>
                ),
              },
              {
                path: 'dtc',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FiveStepEnrollment />
                  </Suspense>
                ),
              },
              {
                path: 'flex',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FlexEnrollment />
                  </Suspense>
                ),
              },
              {
                path: 'light',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <LightEnrollment />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'creating-dashboard',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CreatingDashboard />
              </Suspense>
            ),
          },
          {
            path: 'sign-in',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SignIn />
                  </Suspense>
                ),
              },
              {
                path: 'verify-user',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <VerifyToken />
                  </Suspense>
                ),
              },
              {
                path: 'set-password',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <VerifyToken />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'forgot-password',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ForgotPassword />
              </Suspense>
            ),
          },
          {
            path: 'check-email',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CheckYourEmail />
              </Suspense>
            ),
          },
          {
            path: 'verify-identity',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VerifyYourIdentity />
              </Suspense>
            ),
          },
          {
            path: 'reset-password',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            ),
          },
          {
            path: 'create-password',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CreateNewPassword />
              </Suspense>
            ),
          },
          {
            path: 'faq',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EnrollmentFAQ />
              </Suspense>
            ),
          },
          {
            path: 'error-page',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ErrorPageLayout />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Navigate to='something-went-wrong' replace />
                  </Suspense>
                ),
              },
              {
                path: 'something-went-wrong',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SomethingWentWrong />
                  </Suspense>
                ),
              },
              {
                path: 'offer-not-found',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <OfferNotFound />
                  </Suspense>
                ),
              },
              {
                path: 'brand-not-matched',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BrandNotMatched />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to='sign-in' replace />,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <BrandLoader />,
    errorElement: <AppErrorBoundary />,
    children: [
      {
        path: 'thank-you-download',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ThankYouDownload />
          </Suspense>
        ),
      },
      {
        path: 'dw-email-verification',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EmailVerification />
          </Suspense>
        ),
      },
      {
        path: 'terms-of-service',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TermsOfService />
          </Suspense>
        ),
      },
      {
        path: 'privacy-policy',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: 'download-password-defense',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DownloadPasswordDefense />
          </Suspense>
        ),
      },
    ],
  },
]);

export default routes;
